import type { ConceptEdge } from '@arianna/shared'
import { relationFamily, relationPhrase, type MapLocale, type RelationFamily } from './families.js'
import type { ChapterGraph } from './types.js'

export interface Proposition {
  edgeId: string
  /** `null` per un relationType sconosciuto: il pannello lo mostra senza colore di famiglia. */
  family: RelationFamily | null
  text: string
  resting: boolean
  external: boolean
  otherNodeId: string
}

function byConfidenceDescThenEdgeIdAsc(a: ConceptEdge, b: ConceptEdge): number {
  if (a.confidence !== b.confidence) return b.confidence - a.confidence
  return a.edgeId < b.edgeId ? -1 : a.edgeId > b.edgeId ? 1 : 0
}

/**
 * Itera `graph.edges` (intra + cross incidenti al nodo, NON gli esterni dedotti
 * da `computeFocus`): una frase per arco. Archi con famiglia sconosciuta sono
 * inclusi con il tipo grezzo come frase (solo qui: sono esclusi da layout,
 * visibilità, filtro e fuoco).
 */
export function buildPropositions(
  graph: ChapterGraph,
  nodeId: string,
  locale: MapLocale,
  resting: Set<string>,
): Proposition[] {
  const labelById = new Map<string, string>()
  for (const n of graph.nodes) labelById.set(n.nodeId, n.label)
  for (const n of graph.externalNodes) labelById.set(n.nodeId, n.label)
  const nodeIds = new Set(graph.nodes.map((n) => n.nodeId))

  const incident = graph.edges.filter((e) => e.sourceNodeId === nodeId || e.targetNodeId === nodeId)
  const sorted = [...incident].sort(byConfidenceDescThenEdgeIdAsc)

  const propositions: Proposition[] = []
  for (const e of sorted) {
    const family = relationFamily(e.relationType)
    const otherNodeId = e.sourceNodeId === nodeId ? e.targetNodeId : e.sourceNodeId

    // A = source, B = target; per 'opposes' (simmetrica, senza verso proprio)
    // il fuoco va sempre come A.
    const isOpposes = e.relationType === 'opposes'
    const sourceId = isOpposes ? nodeId : e.sourceNodeId
    const targetId = isOpposes ? otherNodeId : e.targetNodeId
    const labelA = labelById.get(sourceId) ?? sourceId
    const labelB = labelById.get(targetId) ?? targetId

    const phrase = relationPhrase(e.relationType, locale)
    const text = `${labelA} ${phrase} ${labelB}`

    propositions.push({
      edgeId: e.edgeId,
      family,
      text,
      resting: resting.has(e.edgeId),
      external: !nodeIds.has(otherNodeId),
      otherNodeId,
    })
  }

  return propositions
}
